import {
  Identifiable,
  Planzone,
  SecondaryCriteriaTemplate,
  SecondaryCriteria,
  SecondaryCriteriaElementType,
  Project,
} from "@planzone/dto";
import { HttpClient, HttpMethods } from "@planzone/services";

function generateUuid() {
  return `${Math.random().toString(16).slice(2)}-${Math.random()
    .toString(16)
    .slice(2)}-${Math.random().toString(16).slice(2)}-${Math.random()
    .toString(16)
    .slice(2)}`;
}

function assignId(
  criteria: SecondaryCriteria | Identifiable<SecondaryCriteria>,
): Identifiable<SecondaryCriteria> {
  return !("uuid" in criteria) || !criteria.uuid
    ? { ...criteria, uuid: generateUuid() }
    : criteria;
}

function cleanTemplate(
  template: SecondaryCriteriaTemplate,
): SecondaryCriteriaTemplate {
  return Object.values(template).reduce<SecondaryCriteriaTemplate>(
    (nextTemplate, criteria) =>
      [
        ...nextTemplate,
        Object.entries(criteria).reduce<Identifiable<SecondaryCriteria>>(
          (nextCriteria, [key, value]) =>
            key !== "originalRef"
              ? {
                  ...nextCriteria,
                  [key]:
                    key === "options"
                      ? cleanTemplate(value as SecondaryCriteria["options"])
                      : value,
                }
              : nextCriteria,
          {} as Identifiable<SecondaryCriteria>,
        ),
      ] as SecondaryCriteriaTemplate,
    [],
  );
}

function assignIDs(
  template: SecondaryCriteriaTemplate,
): SecondaryCriteriaTemplate {
  return template.map(assignId).map((criteria) => ({
    ...criteria,
    options: criteria.options.map(assignId),
  }));
}

function updateProjectsSecondaryCriteriaTemplate(httpClient: HttpClient) {
  return (
    namespace: string,
    scope: "planzone" | "project",
    template: SecondaryCriteriaTemplate,
  ) => {
    const persistedTemplate = assignIDs(cleanTemplate(template));

    return httpClient
      .sendHttpRequest<Planzone | Project>({
        endpoint: `/${scope === "planzone" ? "planzones" : "projects"}/${namespace}`,
        method: HttpMethods.PATCH,
        data: {
          data: {
            secondaryProjectsCriteriaTemplate:
              JSON.stringify(persistedTemplate),
          },
        },
      })
      .then(() => persistedTemplate);
  };
}

function updateResourcesSecondaryCriteriaTemplate(httpClient: HttpClient) {
  return (
    namespace: string,
    scope: "planzone" | "project",
    template: SecondaryCriteriaTemplate,
  ) => {
    const persistedTemplate = assignIDs(cleanTemplate(template));

    return httpClient
      .sendHttpRequest<Planzone | Project>({
        endpoint: `/${scope === "planzone" ? "planzones" : "projects"}/${namespace}`,
        method: HttpMethods.PATCH,
        data: {
          data: {
            secondaryResourcesCriteriaTemplate:
              JSON.stringify(persistedTemplate),
          },
        },
      })
      .then(() => persistedTemplate);
  };
}

function updateProjectElementsSecondaryCriteriaTemplate(
  httpClient: HttpClient,
) {
  return (
    namespace: string,
    scope: "planzone" | "project",
    template: SecondaryCriteriaTemplate,
  ) => {
    const persistedTemplate = assignIDs(cleanTemplate(template));

    return httpClient
      .sendHttpRequest<Planzone | Project>({
        endpoint: `/${scope === "planzone" ? "planzones" : "projects"}/${namespace}`,
        method: HttpMethods.PATCH,
        data: {
          data: {
            secondaryProjectElementsCriteriaTemplate:
              JSON.stringify(persistedTemplate),
          },
        },
      })
      .then(() => persistedTemplate);
  };
}

function updateProjectReportsSecondaryCriteriaTemplate(httpClient: HttpClient) {
  return (
    namespace: string,
    scope: "planzone" | "project",
    template: SecondaryCriteriaTemplate,
  ) => {
    const persistedTemplate = assignIDs(cleanTemplate(template));

    return httpClient
      .sendHttpRequest<Planzone | Project>({
        endpoint: `/${scope === "planzone" ? "planzones" : "projects"}/${namespace}`,
        method: HttpMethods.PATCH,
        data: {
          data: {
            secondaryProjectReportsCriteriaTemplate:
              JSON.stringify(persistedTemplate),
          },
        },
      })
      .then(() => persistedTemplate);
  };
}

function updateTimeRecordsSecondaryCriteriaTemplate(httpClient: HttpClient) {
  return (
    namespace: string,
    scope: "planzone" | "project",
    template: SecondaryCriteriaTemplate,
  ) => {
    const persistedTemplate = assignIDs(cleanTemplate(template));

    return httpClient
      .sendHttpRequest<Planzone | Project>({
        endpoint: `/${scope === "planzone" ? "planzones" : "projects"}/${namespace}`,
        method: HttpMethods.PATCH,
        data: {
          data: {
            secondaryTimeRecordsCriteriaTemplate:
              JSON.stringify(persistedTemplate),
          },
        },
      })
      .then(() => persistedTemplate);
  };
}

function updateSecondaryCriteriaTemplate(httpClient: HttpClient) {
  return (
    namespace: string,
    scope: "planzone" | "project",
    elementType: SecondaryCriteriaElementType,
    template: SecondaryCriteriaTemplate,
  ) => {
    return elementType === "project"
      ? updateProjectsSecondaryCriteriaTemplate(httpClient)(
          namespace,
          scope,
          template,
        )
      : elementType === "resource"
        ? updateResourcesSecondaryCriteriaTemplate(httpClient)(
            namespace,
            scope,
            template,
          )
        : elementType === "projectReport"
          ? updateProjectReportsSecondaryCriteriaTemplate(httpClient)(
              namespace,
              scope,
              template,
            )
          : elementType === "timeRecord"
            ? updateTimeRecordsSecondaryCriteriaTemplate(httpClient)(
                namespace,
                scope,
                template,
              )
            : updateProjectElementsSecondaryCriteriaTemplate(httpClient)(
                namespace,
                scope,
                template,
              );
  };
}

export { updateSecondaryCriteriaTemplate };
